import { callMethod } from './http/method.js';

const METHOD_CURRENT = 'cita_events.api.organizer.subscription.entitlements.get';
const METHOD_RESOLVE = null;
const DEFAULT_KEY = '__default__';
const CACHE_TTL_MS = 2 * 60 * 1000;

const caches = new Map();
const aliases = new Map();
const pending = new Map();
const subscribers = new Set();
let activeKey = null;

const dev = typeof window !== 'undefined' && !!window.dev_server;

const now = () => Date.now();

function isCacheFresh(entry) {
  if (!entry || !entry.fetchedAt) return false;
  return now() - entry.fetchedAt < CACHE_TTL_MS;
}

function slugFor(organizer) {
  if (!organizer) return null;
  if (typeof organizer === 'string') {
    const normalized = organizer.trim();
    return normalized || null;
  }
  if (typeof organizer === 'object') {
    if (organizer.slug) return slugFor(organizer.slug);
    if (organizer.name) return slugFor(organizer.name);
    if (organizer.organizer) return slugFor(organizer.organizer);
    if (organizer.organizer_slug) return slugFor(organizer.organizer_slug);
    // Unknown object shape: do not stringify to avoid "[object Object]" requests
    return null;
  }
  const normalized = String(organizer || '').trim();
  return normalized || null;
}

function keyFor(organizer) {
  const slug = slugFor(organizer);
  if (!slug) return DEFAULT_KEY;
  return slug.toLowerCase();
}

function resolveAliasKey(key) {
  if (!key) return key;
  let current = key;
  const seen = new Set();
  while (aliases.has(current) && !seen.has(current)) {
    seen.add(current);
    current = aliases.get(current);
  }
  return current;
}

function registerAlias(aliasKey, canonicalKey) {
  if (!aliasKey || !canonicalKey) return;
  if (aliasKey === canonicalKey) return;
  aliases.set(aliasKey, canonicalKey);
}

function registerAliasesFromOrganizer(organizer, canonicalKey) {
  const aliasKeys = [];
  if (!organizer || typeof organizer !== 'object') return aliasKeys;
  const slug = slugFor(organizer.slug || organizer.organizer_slug || null);
  const name = slugFor(organizer.name || organizer.organizer_name || organizer.organizer || null);
  if (slug) {
    const aliasKey = keyFor(slug);
    registerAlias(aliasKey, canonicalKey);
    if (aliasKey && aliasKey !== canonicalKey) aliasKeys.push(aliasKey);
  }
  if (name) {
    const aliasKey = keyFor(name);
    registerAlias(aliasKey, canonicalKey);
    if (aliasKey && aliasKey !== canonicalKey) aliasKeys.push(aliasKey);
  }
  return aliasKeys;
}

function resolveKey(organizer) {
  if (organizer) return resolveAliasKey(keyFor(organizer));
  if (activeKey) return resolveAliasKey(activeKey);
  return DEFAULT_KEY;
}

function unwrapEnvelope(payload) {
  let node = payload;
  const visited = new Set();
  while (node && typeof node === 'object' && !Array.isArray(node)) {
    if (visited.has(node)) break;
    visited.add(node);
    if (node.data && typeof node.data === 'object') {
      node = node.data;
      continue;
    }
    if (node.message && typeof node.message === 'object') {
      node = node.message;
      continue;
    }
    break;
  }
  return node || {};
}

function extractSnapshot(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.entitlements && typeof payload.entitlements === 'object') return payload.entitlements;
  if (payload.snapshot && typeof payload.snapshot === 'object') return payload.snapshot;
  if (payload.entitlementsSnapshot && typeof payload.entitlementsSnapshot === 'object') {
    return payload.entitlementsSnapshot;
  }
  if (payload.entitlements_snapshot && typeof payload.entitlements_snapshot === 'object') {
    return payload.entitlements_snapshot;
  }
  if (payload.features && typeof payload.features === 'object') return payload.features;
  return null;
}

function normalizeOrganizer(info, fallbackSlug) {
  if (info && typeof info === 'object') {
    const normalized = { ...info };
    if (!normalized.slug && fallbackSlug) normalized.slug = fallbackSlug;
    return normalized;
  }
  if (fallbackSlug) return { slug: fallbackSlug };
  return null;
}

function buildEntry(payload, { fallbackKey, fallbackSlug, source }) {
  if (!payload) return null;
  const body = unwrapEnvelope(payload);
  const snapshot = extractSnapshot(body);
  if (!snapshot) return null;

  const resolvedAt = body.resolvedAt || body.resolved_at || null;
  const organizerInfo = normalizeOrganizer(body.organizer, fallbackSlug);
  const slug = slugFor(organizerInfo?.slug || organizerInfo?.organizer_slug || null) || fallbackSlug || null;
  const name = slugFor(organizerInfo?.name || organizerInfo?.organizer_name || organizerInfo?.organizer || null) || null;
  const key = slug ? keyFor(slug) : (name ? keyFor(name) : (fallbackKey || DEFAULT_KEY));

  return {
    key,
    organizer: organizerInfo,
    snapshot: { ...snapshot },
    resolvedAt: resolvedAt ? String(resolvedAt) : null,
    source,
    fetchedAt: now(),
  };
}

function notify(entry) {
  if (!entry) return;
  Array.from(subscribers).forEach((subscriber) => {
    const { handler, key } = subscriber;
    if (typeof handler !== 'function') return;
    if (key && key !== entry.key) return;
    try {
      handler(entry);
    } catch (error) {
      if (dev) console.warn('FeatureGate.subscriber_failed', error);
    }
  });
}

function remember(entry) {
  if (!entry || !entry.key) return null;
  const cached = {
    key: entry.key,
    organizer: entry.organizer ? { ...entry.organizer } : null,
    snapshot: entry.snapshot ? { ...entry.snapshot } : {},
    resolvedAt: entry.resolvedAt || null,
    source: entry.source || null,
    fetchedAt: entry.fetchedAt || now(),
  };
  caches.set(entry.key, cached);
  activeKey = entry.key;
  const aliasKeys = registerAliasesFromOrganizer(cached.organizer, cached.key) || [];
  for (const aliasKey of aliasKeys) {
    if (aliasKey && aliasKey !== cached.key) {
      caches.delete(aliasKey);
    }
  }
  notify(cached);
  return cached;
}

function readBootSnapshot(targetKey) {
  if (typeof window === 'undefined') return null;
  const boot = window.citaBootData?.organizerFeatures;
  if (!boot) return null;
  const entry = buildEntry(boot, { fallbackKey: targetKey, source: 'boot' });
  if (!entry) return null;
  if (targetKey && entry.key !== targetKey) {
    const organizer = entry.organizer || {};
    const nameKey = organizer.name || organizer.organizer_name || organizer.organizer
      ? keyFor(organizer.name || organizer.organizer_name || organizer.organizer)
      : null;
    const slugKey = organizer.slug || organizer.organizer_slug
      ? keyFor(organizer.slug || organizer.organizer_slug)
      : null;
    if (targetKey !== nameKey && targetKey !== slugKey) {
      return null;
    }
  }
  return remember(entry);
}

function getCached(key) {
  const cacheKey = resolveAliasKey(key || activeKey);
  if (!cacheKey) return null;
  return caches.get(cacheKey) || null;
}

function pendingFetch(key) {
  return pending.get(key) || null;
}

function setPending(key, promise) {
  if (key) pending.set(key, promise);
}

function clearPending(key) {
  if (key) pending.delete(key);
}

// B5-03: Boolean coercion aligned with Python _coerce_feature_flag()
// Falsy strings: "", "0", "false", "off", "no", "none", "disabled"
// Dict keys: "effective", "enabled", "value", "limit"
const FALSY_STRINGS = new Set(['', '0', 'false', 'off', 'no', 'none', 'disabled']);

function coerceBoolean(value) {
  if (value == null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value > 0 : Boolean(value);
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (FALSY_STRINGS.has(trimmed)) return false;
    return true;
  }
  if (typeof value === 'object') {
    // B5-03: Added "limit" to match Python implementation
    if ('effective' in value) return coerceBoolean(value.effective);
    if ('enabled' in value) return coerceBoolean(value.enabled);
    if ('value' in value) return coerceBoolean(value.value);
    if ('limit' in value) return coerceBoolean(value.limit);
  }
  return Boolean(value);
}

async function fetchFromApi({ organizer, fallbackKey, force = false } = {}) {
  const normalized = slugFor(organizer);
  const key = resolveKey(normalized || organizer);
  const cached = getCached(key);
  if (!force && cached && isCacheFresh(cached)) {
    return cached;
  }
  const existing = pendingFetch(key);
  if (existing) return existing;

  if (typeof callMethod !== 'function') {
    return cached;
  }

  const slug =
    normalized ||
    slugFor(caches.get(key)?.organizer?.slug) ||
    slugFor(caches.get(key)?.organizer?.name) ||
    null;

  if (!slug) {
    return cached;
  }

  const attempts = [];
  if (METHOD_CURRENT) {
    attempts.push({ method: METHOD_CURRENT, args: { organizer: slug }, httpMethod: 'POST' });
  }
  if (METHOD_RESOLVE) {
    attempts.push({ method: METHOD_RESOLVE, args: { organizer: slug }, httpMethod: 'GET' });
  }

  const work = (async () => {
    for (const attempt of attempts) {
      try {
        const payload = await callMethod(attempt.method, attempt.args, { method: attempt.httpMethod || 'POST' });
        const entry = buildEntry(payload, {
          fallbackKey: fallbackKey || key,
          fallbackSlug: slug,
          source: attempt.method,
        });
        if (entry) {
          return remember(entry);
        }
      } catch (error) {
        if (dev) console.warn('FeatureGate.api_failed', attempt.method, error);
      }
    }
    return cached;
  })().finally(() => clearPending(key));

  setPending(key, work);
  return work;
}

function snapshotFor(organizer) {
  const key = resolveKey(slugFor(organizer) || organizer);
  const cache = getCached(key);
  return cache?.snapshot || {};
}

async function init(options = {}) {
  const opts = typeof options === 'string' ? { organizer: options } : { ...options };
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const desiredKey = organizer ? keyFor(organizer) : null;

  let cache = desiredKey ? getCached(desiredKey) : null;
  if (!cache) {
    cache = readBootSnapshot(desiredKey);
  }
  if (!cache && !desiredKey) {
    cache = readBootSnapshot(null) || null;
  }

  const force = opts.forceRefresh === true || opts.refresh === true;
  const shouldRefresh = force || (!cache && opts.fetch !== false);
  if (shouldRefresh) {
    const refreshed = await fetchFromApi({ organizer, fallbackKey: desiredKey, force });
    if (refreshed) {
      cache = refreshed;
    }
  }

  if (cache) {
    activeKey = cache.key;
    return cache;
  }

  return null;
}

async function refresh(options = {}) {
  const opts = typeof options === 'string' ? { organizer: options } : { ...options };
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const force = opts.force === true || opts.forceRefresh === true || opts.refresh === true;
  return fetchFromApi({ organizer, force });
}

function applySnapshot(options = {}) {
  const opts = typeof options === 'string' ? { organizer: options } : { ...options };
  const snapshot = opts.snapshot && typeof opts.snapshot === 'object' ? opts.snapshot : {};
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const entry = buildEntry(
    {
      organizer,
      snapshot,
      resolvedAt: opts.resolvedAt || opts.resolved_at || null,
    },
    {
      fallbackKey: organizer ? keyFor(organizer) : null,
      fallbackSlug: organizer,
      source: opts.source || 'manual',
    },
  );
  if (!entry) return null;
  return remember(entry);
}

function invalidate(options = {}) {
  const opts = typeof options === 'string' ? { organizer: options } : { ...options };
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const key = organizer ? keyFor(organizer) : null;
  if (key) {
    const canonical = resolveAliasKey(key);
    caches.delete(canonical);
    pending.delete(canonical);
    for (const [aliasKey, aliasValue] of aliases.entries()) {
      if (aliasKey === canonical || aliasValue === canonical) {
        aliases.delete(aliasKey);
      }
    }
    if (activeKey === canonical) activeKey = DEFAULT_KEY;
    notify({ key: canonical, organizer: null, snapshot: {}, resolvedAt: null, source: 'invalidate', fetchedAt: now() });
    return;
  }
  caches.clear();
  pending.clear();
  aliases.clear();
  activeKey = DEFAULT_KEY;
  notify({ key: null, organizer: null, snapshot: {}, resolvedAt: null, source: 'invalidate', fetchedAt: now() });
}

function resolveFeatureValue(snapshot, name) {
  if (!snapshot || typeof snapshot !== 'object' || !name) return undefined;
  if (snapshot.effective && Object.prototype.hasOwnProperty.call(snapshot.effective, name)) {
    return snapshot.effective[name];
  }
  if (snapshot.features && Object.prototype.hasOwnProperty.call(snapshot.features, name)) {
    return snapshot.features[name];
  }
  if (snapshot.details?.effective && Object.prototype.hasOwnProperty.call(snapshot.details.effective, name)) {
    return snapshot.details.effective[name];
  }
  if (snapshot.details?.features && Object.prototype.hasOwnProperty.call(snapshot.details.features, name)) {
    return snapshot.details.features[name];
  }
  const hasContainers = Boolean(
    snapshot.effective || snapshot.features || snapshot.details,
  );
  if (!hasContainers && Object.prototype.hasOwnProperty.call(snapshot, name)) {
    return snapshot[name];
  }
  return undefined;
}

function getFeature(name, options = {}) {
  if (!name) return undefined;
  const opts = typeof options === 'string' ? { organizer: options } : options || {};
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const snapshot = snapshotFor(organizer);
  return resolveFeatureValue(snapshot, name);
}

function hasFeature(name, options = {}) {
  const value = getFeature(name, options);
  return coerceBoolean(value);
}

function getSnapshot(options = {}) {
  const opts = typeof options === 'string' ? { organizer: options } : options || {};
  const organizer = slugFor(opts.organizer || opts.slug || null);
  return snapshotFor(organizer);
}

function getActiveOrganizer() {
  const cache = getCached(activeKey || DEFAULT_KEY);
  return cache?.organizer || null;
}

function subscribe(handler, options = {}) {
  if (typeof handler !== 'function') return () => {};
  const opts = typeof options === 'string' ? { organizer: options } : options || {};
  const organizer = slugFor(opts.organizer || opts.slug || null);
  const key = organizer ? resolveAliasKey(keyFor(organizer)) : opts.key || null;
  const entry = { handler, key };
  subscribers.add(entry);
  return () => subscribers.delete(entry);
}

function ensureBoolean(value) {
  if (value == null) return undefined;
  return coerceBoolean(value);
}

function readLimit(snapshot, name) {
  if (!name || !snapshot || typeof snapshot !== 'object') return undefined;
  if (snapshot.effective && Object.prototype.hasOwnProperty.call(snapshot.effective, name)) {
    return snapshot.effective[name];
  }
  if (snapshot.features && Object.prototype.hasOwnProperty.call(snapshot.features, name)) {
    return snapshot.features[name];
  }
  if (snapshot.details?.effective && Object.prototype.hasOwnProperty.call(snapshot.details.effective, name)) {
    return snapshot.details.effective[name];
  }
  if (snapshot.details?.features && Object.prototype.hasOwnProperty.call(snapshot.details.features, name)) {
    return snapshot.details.features[name];
  }
  const hasContainers = Boolean(
    snapshot.effective || snapshot.features || snapshot.details,
  );
  if (!hasContainers && Object.prototype.hasOwnProperty.call(snapshot, name)) {
    return snapshot[name];
  }
  return undefined;
}

function readInvoiceStatus(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const subscription = snapshot.subscription && typeof snapshot.subscription === 'object' ? snapshot.subscription : null;
  if (subscription && subscription.last_invoice_status) return subscription.last_invoice_status;
  if (snapshot.last_invoice_status) return snapshot.last_invoice_status;
  if (snapshot.details?.subscription?.last_invoice_status) {
    return snapshot.details.subscription.last_invoice_status;
  }
  return null;
}

function hasSnapshotFeature(snapshot, name) {
  return resolveFeatureValue(snapshot, name);
}

export const FeatureGate = {
  init,
  refresh,
  hasFeature,
  getFeature,
  getSnapshot,
  getActiveOrganizer,
  applySnapshot,
  invalidate,
  subscribe,
};

export default FeatureGate;

if (typeof window !== 'undefined') {
  window.FeatureGate = FeatureGate;
}

function optionsOrganizer(options = {}) {
  if (!options) return null;
  if (typeof options === 'string') return options;
  if (options.organizer) return options.organizer;
  if (options.slug) return options.slug;
  return null;
}
